import { getMicronutrientDefinition, type MicronutrientCode } from './micronutrient-registry';

export const EU_NUTRITION_DISPLAY_KINDS = [
  'ENERGY_KJ',
  'ENERGY_KCAL',
  'FAT',
  'SATURATED_FAT',
  'CARBOHYDRATE',
  'SUGARS',
  'FIBER',
  'PROTEIN',
  'SALT',
] as const;

export type EuNutritionDisplayKind = (typeof EU_NUTRITION_DISPLAY_KINDS)[number];

export interface EuNutritionDisplayValue {
  kind: 'VALUE';
  value: number;
  decimals: 0 | 1 | 2;
  unit: 'G' | 'KJ' | 'KCAL';
}

export interface EuNutritionZeroValue {
  kind: 'ZERO';
  value: 0;
  decimals: 1 | 2;
  unit: 'G';
}

export interface EuNutritionLessThanValue {
  kind: 'LESS_THAN';
  threshold: number;
  decimals: 1 | 2;
  unit: 'G';
}

export type EuNutritionFormattedValue =
  | EuNutritionDisplayValue
  | EuNutritionZeroValue
  | EuNutritionLessThanValue;

/**
 * Epsilon-safe half-up rounding at an arbitrary positive scale (1 unit =
 * 1/scale). EPSILON protects decimal halves represented infinitesimally below
 * their mathematical value (e.g. 1.005 * 100 evaluating to 100.49999999999999
 * in IEEE 754) without changing other cut-offs. Shared by fixed-decimal
 * rounding (`roundHalfUp`) and significant-digit rounding
 * (`roundToSignificantDigits`) so both apply the identical protection.
 */
function roundHalfUpAtScale(value: number, scale: number): number {
  return Math.floor((value + Number.EPSILON * Math.max(1, value)) * scale + 0.5) / scale;
}

/** Values are non-negative by nutritional-profile invariant. */
function roundHalfUp(value: number, decimals: 0 | 1 | 2): number {
  return roundHalfUpAtScale(value, 10 ** decimals);
}

function value(
  raw: number,
  decimals: 0 | 1 | 2,
  unit: EuNutritionDisplayValue['unit'] = 'G',
): EuNutritionDisplayValue {
  return { kind: 'VALUE', value: roundHalfUp(raw, decimals), decimals, unit };
}

function atOrBelowThreshold(
  rawValue: number,
  threshold: number,
  decimals: 1 | 2,
): EuNutritionZeroValue | EuNutritionLessThanValue {
  return rawValue === 0
    ? { kind: 'ZERO', value: 0, decimals, unit: 'G' }
    : { kind: 'LESS_THAN', threshold, decimals, unit: 'G' };
}

/**
 * Resolves a two-band value (fewer decimals at/above `bandThreshold`, more
 * below it) so the printed decimals always match the printed value's own
 * band — not the raw, unrounded one. Rounding at the "below threshold"
 * precision can itself cross the threshold (e.g. FAT 9.96 -> 10.0 at 1
 * decimal, which belongs in the >=10 band): when that happens, decimals are
 * re-picked from the rounded value and the raw value is rounded again at
 * that precision, matching the DG SANTE declaration-rounding convention in
 * `dg-sante-nutrition-tolerance.ts`. A single re-check always suffices —
 * coarser rounding of a value already at/above the threshold cannot push it
 * back below, so the band can only ever move up, never down.
 */
function bandedValue(
  rawValue: number,
  bandThreshold: number,
  belowThresholdDecimals: 0 | 1 | 2,
  atOrAboveThresholdDecimals: 0 | 1 | 2,
): EuNutritionDisplayValue {
  const initialDecimals =
    rawValue >= bandThreshold ? atOrAboveThresholdDecimals : belowThresholdDecimals;
  const rounded = roundHalfUp(rawValue, initialDecimals);
  const finalDecimals =
    rounded >= bandThreshold ? atOrAboveThresholdDecimals : belowThresholdDecimals;
  if (finalDecimals === initialDecimals) {
    return { kind: 'VALUE', value: rounded, decimals: finalDecimals, unit: 'G' };
  }
  return {
    kind: 'VALUE',
    value: roundHalfUp(rawValue, finalDecimals),
    decimals: finalDecimals,
    unit: 'G',
  };
}

/**
 * Single EU presentation policy for UI, PDFs and exports. It does not modify
 * calculation values: callers pass an already-calculated nutrient and receive
 * a deterministic structured number/token, never localized prose. Threshold
 * bands are selected from the rounded value, so the printed decimals always
 * agree with the printed value's own band (see `bandedValue`).
 */
export function formatEuNutritionValue(
  kind: EuNutritionDisplayKind,
  rawValue: number,
): EuNutritionFormattedValue {
  if (!Number.isFinite(rawValue) || rawValue < 0) {
    throw new RangeError('EU nutrition display value must be a finite non-negative number');
  }
  if (kind === 'ENERGY_KJ') {
    return value(rawValue, 0, 'KJ');
  }
  if (kind === 'ENERGY_KCAL') {
    return value(rawValue, 0, 'KCAL');
  }
  if (kind === 'SALT') {
    if (rawValue <= 0.0125) {
      return atOrBelowThreshold(rawValue, 0.01, 2);
    }
    return bandedValue(rawValue, 1, 2, 1);
  }
  const zeroThreshold = kind === 'SATURATED_FAT' ? 0.1 : 0.5;
  if (rawValue <= zeroThreshold) {
    return atOrBelowThreshold(rawValue, zeroThreshold, 1);
  }
  return bandedValue(rawValue, 10, 1, 0);
}

const EU_NUTRITION_UNIT_LABELS: Record<EuNutritionFormattedValue['unit'], string> = {
  G: 'g',
  KJ: 'kJ',
  KCAL: 'kcal',
};

function formatItalianNumber(rawValue: number, decimals: 0 | 1 | 2): string {
  return new Intl.NumberFormat('it-IT', {
    useGrouping: false,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(rawValue);
}

/**
 * Italian rendering of `formatEuNutritionValue`, unit included. Owns both the
 * decimal comma and the unit label so labels, PDFs and exports cannot drift
 * apart: `toFixed` is locale-invariant and would print an Anglo-Saxon dot on
 * an Italian label, next to micronutrients already formatted with `it-IT`.
 *
 * The separator between value and unit is a plain space, never U+00A0: the
 * PDF word-wrap engine splits on `\s`, which in JS includes the non-breaking
 * space, and would break `4,0 g` across two lines.
 */
export function formatEuNutritionValueIt(kind: EuNutritionDisplayKind, rawValue: number): string {
  const formatted = formatEuNutritionValue(kind, rawValue);
  const unit = EU_NUTRITION_UNIT_LABELS[formatted.unit];
  if (formatted.kind === 'LESS_THAN') {
    return `< ${formatItalianNumber(formatted.threshold, formatted.decimals)} ${unit}`;
  }
  if (formatted.kind === 'ZERO') {
    return `0 ${unit}`;
  }
  return `${formatItalianNumber(formatted.value, formatted.decimals)} ${unit}`;
}

export interface EuMicronutrientDisplayValue {
  kind: 'VALUE';
  value: number;
  unit: 'MG' | 'MCG';
}

/** Rounds to N significant digits (not fixed decimals — DG SANTE Guidance
 * Document Dec. 2012, section 6, Table 4: vitamin A and folic acid get 3
 * significant digits, every other vitamin gets 2). */
function roundToSignificantDigits(rawValue: number, digits: number): number {
  if (rawValue === 0) {
    return 0;
  }
  const magnitude = Math.ceil(Math.log10(Math.abs(rawValue)));
  const scale = 10 ** (digits - magnitude);
  return roundHalfUpAtScale(rawValue, scale);
}

/**
 * Presentation policy for vitamins (Allegato XIII), separate from
 * `formatEuNutritionValue`: unit (mg/mcg) and rounding (significant digits,
 * not fixed decimals) are per-nutrient, taken from the micronutrient registry.
 * `rawValue` is already expressed in the nutrient's canonical unit.
 */
export function formatEuMicronutrientValue(
  code: MicronutrientCode,
  rawValue: number,
): EuMicronutrientDisplayValue {
  if (!Number.isFinite(rawValue) || rawValue < 0) {
    throw new RangeError('EU micronutrient display value must be a finite non-negative number');
  }
  const micronutrientDefinition = getMicronutrientDefinition(code);
  return {
    kind: 'VALUE',
    value: roundToSignificantDigits(rawValue, micronutrientDefinition.significantDigits),
    unit: micronutrientDefinition.unit,
  };
}
